import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { registerSW } from 'virtual:pwa-register'
import { AuthProvider } from './contexts/AuthContext'
import App from './App.tsx'
import SponsorPage from './pages/SponsorPage.tsx'
import SponsorValueReportPage from './pages/SponsorValueReportPage.tsx'
import PreviewPage from './pages/PreviewPage.tsx'
import NotFound from './pages/NotFound.tsx'
import PrivacyPage from './pages/PrivacyPage.tsx'
import TermsPage from './pages/TermsPage.tsx'
import SignInPage from './pages/SignInPage.tsx'
import RequestAccessPage from './pages/RequestAccessPage.tsx'
import AccountPage from './pages/AccountPage.tsx'
import StatusPage from './pages/StatusPage.tsx'
import AdminDashboardPage from './pages/AdminDashboardPage.tsx'
import AdminMembersPage from './pages/AdminMembersPage.tsx'
import AdminContentPage from './pages/AdminContentPage.tsx'
import AdminSponsorPageEditor from './pages/AdminSponsorPageEditor.tsx'
import AdminIntegrationsPage from './pages/AdminIntegrationsPage.tsx'
import AdminKnockoutPage from './pages/AdminKnockoutPage.tsx'
import MasterAudiencePage from './pages/MasterAudiencePage.tsx'
import NotificationsPage from './pages/NotificationsPage.tsx'
import TeamDesignPage from './pages/TeamDesignPage.tsx'
import KnockoutPage from './pages/KnockoutPage.tsx'
import SneakPeekPage from './pages/SneakPeekPage.tsx'

/**
 * Yan-Vada router.
 *
 * Session 15b flow-pass update + Session 16 auth/gates/legal incorporation:
 *   - vada.yan.lk/ root renders the limited public audience page from Master CMS
 *   - Per-sponsor overlays (The Licenced, Route Archive, etc.) produced separately
 *   - /privacy and /terms are public legal pages (no gate required)
 *   - TCGate fires on protected sponsor pitch routes. NDAGate fires after sign-in.
 *   - Core team (@dgtl.lk) bypasses all gates — full access.
 *   - The Licenced v3 config archived (not routed); overlay is future work.
 *
 * Routes:
 *   /                      — Limited public audience LP [public, Master-derived Hero + intro only]
 *   /privacy               — Privacy Policy [public, no gate]
 *   /terms                 — Terms of Use [public, no gate]
 *   /signin                — Sign-in page [public, no gate]
 *   /request-access        — Request access form [public, no gate]
 *   /home                  — Limited public audience LP alias [public, Master-derived Hero + intro only]
 *   /account               — Member account [requires sign-in, no gate]
 *   /notifications         — Member notification centre [requires sign-in, no gate]
 *   /knockout              — Member Knockout Session phone flow [requires knockout.play]
 *   /admin                 — Admin command center for ops readiness
 *   /admin/members         — Admin CMS for member approval and sponsor access
 *   /admin/content         — Legacy public-home CMS [frozen, retained only for backward review]
 *   /admin/sponsor-pages/:slug — Authoritative sponsor-pitch CMS; Master is source-of-truth, sponsor tabs are derived editions
 *   /admin/integrations    — Admin delivery queue for Slack/Notion hooks
 *   /admin/knockout        — Admin workbench for Knockout preview/reset
 *   /status                — Project progress dashboard [public, no gate]
 *   /thelicenced, /the-licenced, /gold, /popculture, /pop-culture, /cameralk, /camera-lk, /cookwarelk, /cookware-lk — simple sponsor fallback copies [public]
 *   /thelicenced/value-report, /the-licenced/value-report, /gold/value-report, /popculture/value-report, /pop-culture/value-report, /cameralk/value-report, /camera-lk/value-report, /cookwarelk/value-report, /cookware-lk/value-report — simple value-report fallbacks [public]
 *   /:slug                 — protected per-sponsor pitch fallback for unmatched short paths [TCGate + NDAGate]
 *   /sponsors/:slug        — per-sponsor pitch (explicit path, kept for compat)
 *   /preview/:slug         — per-sponsor preview (from /draft/ configs, pre-ratification)
 *   *                      — 404
 */

let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | undefined

const isLocalPreviewHost = ['localhost', '127.0.0.1'].includes(window.location.hostname)

if (isLocalPreviewHost) {
  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.getRegistrations().then((registrations) => {
      return Promise.all(registrations.map((registration) => registration.unregister()))
    }).catch((error) => {
      console.warn('Failed to unregister local service workers', error)
    })
  }
  if ('caches' in window) {
    void caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key)))).catch((error) => {
      console.warn('Failed to clear local caches', error)
    })
  }
} else {
  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      void updateServiceWorker?.(true)
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      void registration.update()
      window.addEventListener('focus', () => {
        void registration.update()
      })
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void registration.update()
      })
      window.setInterval(() => {
        void registration.update()
      }, 5 * 60 * 1000)
    },
    onRegisterError(error) {
      console.warn('Service worker registration failed', error)
    },
  })
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        <App />
      </AuthProvider>
    ),
    children: [
      { index: true, element: <MasterAudiencePage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'signin', element: <SignInPage /> },
      { path: 'request-access', element: <RequestAccessPage /> },
      { path: 'home', element: <MasterAudiencePage /> },
      { path: 'prospect', element: <SponsorPage defaultSlug="prospect" publicProspect /> },
      { path: 'prospect/value-report', element: <SponsorValueReportPage defaultSlug="prospect" publicProspect /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'knockout', element: <KnockoutPage /> },
      { path: 'watch/sneak-peek', element: <SneakPeekPage /> },
      { path: 'media/yan-vada-sneak-peek.mp4', element: <SneakPeekPage /> },
      { path: 'team/design', element: <TeamDesignPage /> },
      { path: 'admin', element: <AdminDashboardPage /> },
      { path: 'admin/members', element: <AdminMembersPage /> },
      { path: 'admin/content', element: <AdminContentPage /> },
      { path: 'admin/sponsor-pages/:slug', element: <AdminSponsorPageEditor /> },
      { path: 'admin/integrations', element: <AdminIntegrationsPage /> },
      { path: 'admin/knockout', element: <AdminKnockoutPage /> },
      { path: 'status', element: <StatusPage /> },
      { path: 'temporary/the-licenced-sponsor-copy-2026-05-27', element: <SponsorPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'temporary/the-licenced-sponsor-copy-2026-05-27.html', element: <SponsorPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'temporary/the-licenced-value-report-copy-2026-05-27', element: <SponsorValueReportPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'temporary/the-licenced-value-report-copy-2026-05-27.html', element: <SponsorValueReportPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'temporary/gold-sponsor-copy-2026-06-07', element: <SponsorPage defaultSlug="gold" publicCopy /> },
      { path: 'temporary/gold-sponsor-copy-2026-06-07.html', element: <SponsorPage defaultSlug="gold" publicCopy /> },
      { path: 'temporary/gold-value-report-copy-2026-06-07', element: <SponsorValueReportPage defaultSlug="gold" publicCopy /> },
      { path: 'temporary/gold-value-report-copy-2026-06-07.html', element: <SponsorValueReportPage defaultSlug="gold" publicCopy /> },
      { path: 'temporary/pop-culture-sponsor-copy-2026-06-07', element: <SponsorPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'temporary/pop-culture-sponsor-copy-2026-06-07.html', element: <SponsorPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'temporary/pop-culture-value-report-copy-2026-06-07', element: <SponsorValueReportPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'temporary/pop-culture-value-report-copy-2026-06-07.html', element: <SponsorValueReportPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'temporary/camera-lk-sponsor-copy-2026-06-07', element: <SponsorPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'temporary/camera-lk-sponsor-copy-2026-06-07.html', element: <SponsorPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'temporary/camera-lk-value-report-copy-2026-06-07', element: <SponsorValueReportPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'temporary/camera-lk-value-report-copy-2026-06-07.html', element: <SponsorValueReportPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'temporary/cookware-lk-sponsor-copy-2026-06-25', element: <SponsorPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'temporary/cookware-lk-sponsor-copy-2026-06-25.html', element: <SponsorPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'temporary/cookware-lk-value-report-copy-2026-06-25', element: <SponsorValueReportPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'temporary/cookware-lk-value-report-copy-2026-06-25.html', element: <SponsorValueReportPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'thelicenced', element: <SponsorPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'thelicenced/value-report', element: <SponsorValueReportPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'the-licenced', element: <SponsorPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'the-licenced/value-report', element: <SponsorValueReportPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'thelicensed', element: <SponsorPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'thelicensed/value-report', element: <SponsorValueReportPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'the-licensed', element: <SponsorPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'the-licensed/value-report', element: <SponsorValueReportPage defaultSlug="the-licenced" publicCopy /> },
      { path: 'gold', element: <SponsorPage defaultSlug="gold" publicCopy /> },
      { path: 'gold/value-report', element: <SponsorValueReportPage defaultSlug="gold" publicCopy /> },
      { path: 'goldbyhemas', element: <SponsorPage defaultSlug="gold" publicCopy /> },
      { path: 'goldbyhemas/value-report', element: <SponsorValueReportPage defaultSlug="gold" publicCopy /> },
      { path: 'gold-by-hemas', element: <SponsorPage defaultSlug="gold" publicCopy /> },
      { path: 'gold-by-hemas/value-report', element: <SponsorValueReportPage defaultSlug="gold" publicCopy /> },
      { path: 'popculture', element: <SponsorPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'popculture/value-report', element: <SponsorValueReportPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'pop-culture', element: <SponsorPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'pop-culture/value-report', element: <SponsorValueReportPage defaultSlug="pop-culture" publicCopy /> },
      { path: 'cameralk', element: <SponsorPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'cameralk/value-report', element: <SponsorValueReportPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'camera-lk', element: <SponsorPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'camera-lk/value-report', element: <SponsorValueReportPage defaultSlug="camera-lk" publicCopy /> },
      { path: 'cookwarelk', element: <SponsorPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'cookwarelk/value-report', element: <SponsorValueReportPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'cookware-lk', element: <SponsorPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'cookware-lk/value-report', element: <SponsorValueReportPage defaultSlug="cookware-lk" publicCopy /> },
      { path: 'sponsors/:slug/value-report', element: <SponsorValueReportPage /> },
      { path: 'sponsors/:slug', element: <SponsorPage /> },
      { path: 'preview/:slug', element: <PreviewPage /> },
      { path: ':slug', element: <SponsorPage /> },
      { path: '*', element: <NotFound /> }
    ]
  }
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
)
